import { motion, useAnimation } from "framer-motion";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

// Adaptado de pqoqubbw/icons a JS (CRA) + framer-motion.
// Uso simple:  <CircleDollarSignIcon size={16} />
// Uso controlado (animar desde el padre):
//   const ref = useRef(); ref.current.startAnimation() / stopAnimation()

const DOLLAR_MAIN_VARIANTS = {
  normal: {
    opacity: 1,
    pathLength: 1,
    transition: { duration: 0.4, opacity: { duration: 0.1 } },
  },
  animate: {
    opacity: [0, 1],
    pathLength: [0, 1],
    transition: { duration: 0.6, opacity: { duration: 0.1 } },
  },
};

const DOLLAR_SECONDARY_VARIANTS = {
  normal: {
    opacity: 1,
    pathLength: 1,
    pathOffset: 0,
    transition: { delay: 0.3, duration: 0.3, opacity: { duration: 0.1, delay: 0.3 } },
  },
  animate: {
    opacity: [0, 1],
    pathLength: [0, 1],
    pathOffset: [1, 0],
    transition: { delay: 0.5, duration: 0.4, opacity: { duration: 0.1, delay: 0.5 } },
  },
};

const CircleDollarSignIcon = forwardRef(
  ({ onMouseEnter, onMouseLeave, className, size = 28, ...props }, ref) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;
      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    const handleMouseEnter = useCallback(
      (e) => {
        if (isControlledRef.current) {
          onMouseEnter?.(e);
        } else {
          controls.start("animate");
        }
      },
      [controls, onMouseEnter]
    );

    const handleMouseLeave = useCallback(
      (e) => {
        if (isControlledRef.current) {
          onMouseLeave?.(e);
        } else {
          controls.start("normal");
        }
      },
      [controls, onMouseLeave]
    );

    return (
      <div
        className={className}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <svg
          fill="none"
          height={size}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="12" r="10" />
          <motion.path
            animate={controls}
            d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"
            initial="normal"
            variants={DOLLAR_MAIN_VARIANTS}
          />
          <motion.path
            animate={controls}
            d="M12 18V6"
            initial="normal"
            variants={DOLLAR_SECONDARY_VARIANTS}
          />
        </svg>
      </div>
    );
  }
);

CircleDollarSignIcon.displayName = "CircleDollarSignIcon";

export { CircleDollarSignIcon };
